import React, { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { clearSession, heroApiUrl, setSession, AUTH_USE_HTTPONLY_COOKIE } from "../utils/auth";

const authRequestConfig = () =>
  AUTH_USE_HTTPONLY_COOKIE ? { withCredentials: true } : {};

const STEPS = {
  CREDENTIALS: "credentials",
  SETUP: "setup",
  VERIFY: "verify",
};

function Login() {
  const [step, setStep] = useState(STEPS.CREDENTIALS);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState("");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    clearSession();
  }, []);

  useEffect(() => {
    if (step !== STEPS.SETUP || !preAuthToken) return;

    let cancelled = false;
    const loadSetup = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(
          heroApiUrl("/api/auth/2fa/setup"),
          {},
          {
            headers: { Authorization: `Bearer ${preAuthToken}` },
            ...authRequestConfig(),
          }
        );
        if (cancelled) return;
        setOtpauthUrl(res.data.otpauthUrl || "");
        setManualKey(res.data.manualKey || "");
      } catch (err) {
        if (!cancelled) {
          setError(
            (err.response && err.response.data && err.response.data.message) ||
              "Could not start two-factor setup. Try logging in again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSetup();
    return () => {
      cancelled = true;
    };
  }, [step, preAuthToken]);

  const finishLogin = (data) => {
    if (!data.isAdmin || !data.token) {
      clearSession();
      setError("Check credentials and try again");
      return false;
    }
    setSession({
      token: data.token,
      email: data.email,
      isAdmin: data.isAdmin,
    });
    navigate("/home");
    return true;
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const user = {
      email: e.target[0].value,
      password: e.target[1].value,
    };

    try {
      const res = await axios.post(
        heroApiUrl("/api/auth/login"),
        user,
        authRequestConfig()
      );

      if (res.data.requiresTwoFactorSetup && res.data.preAuthToken) {
        setTotpCode("");
        setPreAuthToken(res.data.preAuthToken);
        setStep(STEPS.SETUP);
        setLoading(false);
        return;
      }

      if (res.data.requiresTwoFactor && res.data.preAuthToken) {
        setTotpCode("");
        setPreAuthToken(res.data.preAuthToken);
        setStep(STEPS.VERIFY);
        setLoading(false);
        return;
      }

      if (!finishLogin(res.data)) {
        setLoading(false);
      }
    } catch (err) {
      setError("Check credentials and try again");
      setLoading(false);
    }
  };

  const handleEnable2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        heroApiUrl("/api/auth/2fa/enable"),
        { code: totpCode },
        {
          headers: { Authorization: `Bearer ${preAuthToken}` },
          ...authRequestConfig(),
        }
      );
      if (!finishLogin(res.data)) {
        setLoading(false);
      }
    } catch (err) {
      setError(
        (err.response && err.response.data && err.response.data.message) ||
          "Invalid authenticator code"
      );
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        heroApiUrl("/api/auth/2fa/verify"),
        { code: totpCode },
        {
          headers: { Authorization: `Bearer ${preAuthToken}` },
          ...authRequestConfig(),
        }
      );
      if (!finishLogin(res.data)) {
        setLoading(false);
      }
    } catch (err) {
      setError(
        (err.response && err.response.data && err.response.data.message) ||
          "Invalid authenticator code"
      );
      setLoading(false);
    }
  };

  const panelClass =
    "bg-black w-[90%] sm:w-[50%] min-h-[50%] flex flex-col items-center justify-center rounded-[15px] py-8 px-4";

  return (
    <div className="bg-[#FFA500] w-full min-h-[100vh] flex items-center justify-center py-8">
      <div className={panelClass}>
        {step === STEPS.CREDENTIALS && (
          <form
            onSubmit={handleCredentialsSubmit}
            className="flex flex-col gap-5 w-[90%] sm:w-[50%]"
          >
            <div>
              <input
                type="email"
                placeholder="Email"
                className="px-1.5 py-2.5 w-[100%] outline-none"
              />
            </div>
            <div>
              <input
                type="password"
                placeholder="Password"
                className="px-1.5 py-2.5 w-[100%] outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-[#359602] disabled:bg-gray-500 text-xl text-white py-2 hover:bg-white hover:text-[#1f2937]"
            >
              {loading ? "Please wait..." : "Login"}
            </button>
          </form>
        )}

        {step === STEPS.SETUP && (
          <div className="flex flex-col items-center gap-4 w-[90%] sm:w-[70%] text-white text-center">
            <h2 className="text-lg font-semibold">Set up two-factor authentication</h2>
            <p className="text-sm text-gray-300">
              Scan this QR code with Google Authenticator, Authy, or a similar app.
              You must complete setup before using the admin panel.
            </p>
            {otpauthUrl && (
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG value={otpauthUrl} size={180} />
              </div>
            )}
            {manualKey && (
              <p className="text-xs break-all text-gray-400">
                Manual key: <span className="text-white font-mono">{manualKey}</span>
              </p>
            )}
            <form onSubmit={handleEnable2fa} className="flex flex-col gap-3 w-full max-w-xs">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                className="px-1.5 py-2.5 w-[100%] outline-none text-black text-center tracking-widest"
              />
              <button
                type="submit"
                disabled={loading || totpCode.length < 6}
                className="bg-[#359602] disabled:bg-gray-500 text-xl text-white py-2 hover:bg-white hover:text-[#1f2937]"
              >
                {loading ? "Please wait..." : "Enable 2FA & continue"}
              </button>
            </form>
          </div>
        )}

        {step === STEPS.VERIFY && (
          <div className="flex flex-col items-center gap-4 w-[90%] sm:w-[50%] text-white text-center">
            <h2 className="text-lg font-semibold">Two-factor authentication</h2>
            <p className="text-sm text-gray-300">
              Enter the 6-digit code from your authenticator app.
            </p>
            <form onSubmit={handleVerify2fa} className="flex flex-col gap-3 w-full">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                className="px-1.5 py-2.5 w-[100%] outline-none text-black text-center tracking-widest"
              />
              <button
                type="submit"
                disabled={loading || totpCode.length < 6}
                className="bg-[#359602] disabled:bg-gray-500 text-xl text-white py-2 hover:bg-white hover:text-[#1f2937]"
              >
                {loading ? "Please wait..." : "Verify"}
              </button>
            </form>
          </div>
        )}

        {error && <p className="text-red-500 mt-3 px-4 text-center">{error}</p>}
      </div>
    </div>
  );
}

export default Login;
